import {createBrowserRouter} from 'react-router-dom';
import {GuestRoute} from '@common/auth/guards/guest-route';
import {authGuard} from '@common/auth/guards/auth-route';
import React from 'react';
import {authRoutes} from '@common/auth/auth-routes';
import {notificationRoutes} from '@common/notifications/notification-routes';
import {DynamicHomepage} from '@common/ui/other/dynamic-homepage';
import {RootErrorElement, RootRoute} from '@common/core/common-provider';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {LandingPage} from '@app/landing/landing-page';
import {driveRoutes} from '@app/drive/drive-routes';
import {checkoutRoutes} from '@common/billing/checkout/routes/checkout-routes';
import {billingPageRoutes} from '@common/billing/billing-page/routes/billing-page-routes';
import {commonRoutes} from '@common/core/common-routes';
import {adminRoutes} from '@common/admin/routes/admin-routes';
import {ExpiredSubscriptionsIndexPage} from '@common/admin/expired subscriptions/expired-subscriptions-index-page';
import { ContentCreators } from '@app/content-creators/cloud-storage-for-content-creators';
import { AboutUs } from '@app/about-us/about-us';
import { DigitalMarketing } from '@app/digital-marketing/cloud-storage-for-digital-marketing';
import { Photographers } from '@app/photographers/cloud-storage-for-photographers';
import { Photos } from '@app/photos/cloud-storage-for-photos';
import { Youtubers } from '@app/youtubers/cloud-storage-for-youtubers';
import { VideoCreators } from '@app/video-creators/cloud-storage-for-video-creators';
import { Solution } from '@app/solutions/cloud-storage-solutions';
import { Education } from '@app/education/cloud-storage-for-education';
import { BusinessStorage } from '@app/busniess-cloud-storage/business-cloud-storage';
import { SeoSpecialist } from '@app/seo-specialists/cloud-storage-for-seo-specialists';
import { SocialMediaManagers } from '@app/social-media-managers/cloud-storage-for-social-media-managers';
import { Pendrive } from '@app/pentdrive/pendrive-alternative-cloud-storage';
import { Harddrive } from '@app/hard-drive/hard-drive-alternative-cloud-storage';
import { Designers } from '@app/designers/cloud-storage-for-designers';
import { BrandCreators } from '@app/brand-creators/cloud-storage-for-brand-creators';
import { Enterprise } from '@app/enterprise/cloud-storage-for-enterprise';
import { ConfirmPlanRenewalPage } from '@common/billing/billing-page/routes/billing-page-routes.lazy';
import { GoogleDriveAlternative } from '@app/google-drive-alternative/smart-google-drive-alternative';
import { ProtonDriveAlternative } from '@app/best-proton-drive-alternative/best-proton-drive-alternative';
import { DropboxAlternative } from '@app/dropbox-alternative-fast-secure-cloud-storage/dropbox-alternative-fast-secure-cloud-storage';
import { BestCloudStorage } from '@app/best-cloud-storage/best-cloud-storage';
import { TeraboxAlternative } from '@app/terabox-alternative/terabox-alternative';
import { CloudStoragePricing } from '@app/cloud-storage-pricing/cloud-storage-pricing';
import { SecureCloudStorage } from '@app/secure-cloud-storage/secure-cloud-storage';
import { BestSecureCloudStorage } from '@app/best-secure-cloud-storage/best-secure-cloud-storage';
import { IndianCloudStorage } from '@app/indian-cloud-storage/indian-cloud-storage';
import { CloudStorageServices } from '@app/cloud-storage-services/cloud-storage-services';
import { BestOneDriveAlternative } from '@app/best-onedrive-alternative/best-onedrive-alternative';
import { BestCloudStorageInIndia } from '@app/best-cloud-storage-in-india/best-cloud-storage-in-india';
import { CloudStorage } from '@app/cloud-storage/cloud-storage';
import { FreeCloudStorage } from '@app/free-cloud-storage/free-cloud-storage';
import { PersonalCloudStorage } from '@app/personal-cloud-storage/personal-cloud-storage';
import {useEffect} from 'react';
import {FileUploadProvider, useFileUploadStore} from '@common/uploads/uploader/file-upload-provider';
import {UploadQueue} from '@app/drive/uploading/upload-queue';
import {uploadStoreOptions} from '@app/drive/uploading/upload-store-options';

function AppRootRoute() {
  return (
    <FileUploadProvider options={uploadStoreOptions}>
      <RootRoute />
      <UploadQueue />
      <GlobalUploadBeforeUnloadGuard />
    </FileUploadProvider>
  );
}

function GlobalUploadBeforeUnloadGuard() {
  const fileUploads = useFileUploadStore(s => s.fileUploads);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasActive = [...(fileUploads?.values?.() ?? [])].some(
        upload =>
          upload.status !== 'completed' &&
          upload.status !== 'failed' &&
          upload.status !== 'aborted',
      );

      if (hasActive) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [fileUploads]);

  return null;
}


export const appRouter = createBrowserRouter(
  [
    {
      id: 'root',
      element: <AppRootRoute />,
      errorElement: <RootErrorElement />,
      children: [
        {
          path: '/',
          element: (
            <DynamicHomepage
              homepageResolver={() => (
                <GuestRoute>
                  <LandingPage />
                </GuestRoute>
              )}
            />
          ),
        },
        ...driveRoutes,
        ...adminRoutes,
        ...authRoutes,
        ...notificationRoutes,
        ...checkoutRoutes,
        ...billingPageRoutes,
        ...commonRoutes,

        {
          path: 'cloud-storage-for-content-creators',
          element: <ContentCreators />,
        },
        {
          path: 'about-us',
          element: <AboutUs />,
        },
        {
          path: 'cloud-storage-for-digital-marketing',
          element: <DigitalMarketing />,
        },
        {
          path: 'cloud-storage-for-photographers',
          element: <Photographers />,
        },
        {
          path: 'cloud-storage-for-photos',
          element: <Photos />,
        },
        {
          path: 'cloud-storage-for-youtubers',
          element: <Youtubers />,
        },
        {
          path: 'cloud-storage-for-video-creators',
          element: <VideoCreators />
        },
        {
          path: 'cloud-storage-solutions',
          element: <Solution />
        }, 
        {
          path: 'cloud-storage-for-education',
          element: <Education />
        }, 
        {
          path: 'business-cloud-storage',
          element: <BusinessStorage />
        }, 
        {
          path: 'cloud-storage-for-seo-specialists',
          element: <SeoSpecialist />
        },  
         {
          path: 'cloud-storage-for-social-media-managers',
          element: <SocialMediaManagers />
        },
        {
          path: 'cloud-storage-for-designers',
          element: <Designers />
        },
        {
          path: 'cloud-storage-for-brand-creators',
          element: <BrandCreators />
        },
        {
          path: 'cloud-storage-for-enterprise',
          element: <Enterprise />
        },
        {
          path: 'pendrive-alternative-cloud-storage',
          element: <Pendrive />
        },
        {
          path: 'hard-drive-alternative-cloud-storage',
          element: <Harddrive />
        },
        {
          path: "/billing/confirm-plan-renew-page",
          element: <ConfirmPlanRenewalPage  />
        },
        {
          path: 'smart-google-drive-alternative',
          element: <GoogleDriveAlternative />
        },
        {
          path: 'best-proton-drive-alternative',
          element: <ProtonDriveAlternative />
        },
        {
          path: 'dropbox-alternative-fast-secure-cloud-storage',
          element: <DropboxAlternative />
        },
        {
          path: 'best-cloud-storage',
          element: <BestCloudStorage />
        },
        {
          path: 'terabox-alternative',
          element: <TeraboxAlternative />
        },
        {
          path: 'cloud-storage-pricing',
          element: <CloudStoragePricing />
        },
        {
          path: 'secure-cloud-storage',
          element: <SecureCloudStorage />
        },
        {
          path: 'best-secure-cloud-storage',
          element: <BestSecureCloudStorage />
        },
        {
          path: 'indian-cloud-storage',
          element: <IndianCloudStorage />
        },
        {
          path: 'cloud-storage-services',
          element: <CloudStorageServices />
        },
        {
          path: 'best-onedrive-alternative',
          element: <BestOneDriveAlternative />
        },
        {
          path: 'best-cloud-storage-in-india',
          element: <BestCloudStorageInIndia />
        },
        {
          path: 'cloud-storage',
          element: <CloudStorage />
        },
        {
          path: 'free-cloud-storage',
          element: <FreeCloudStorage />
        },
        {
          path: 'personal-cloud-storage',
          element: <PersonalCloudStorage />
        },
        {
          path: 'api-docs',
          loader: () =>
            authGuard({permission: 'api.access', requireLogin: false}),
          lazy: () => import('@common/swagger/swagger-api-docs-page'),
        },
       
      ],
    },
  ],
  {
    basename: getBootstrapData().settings.html_base_uri,
  },
);
